import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Users,
  Wallet, 
  Search, 
  Plus, 
  FileText, 
  Download, 
  Check, 
  Filter,
  TrendingUp,
  DollarSign,
  Calendar,
  Eye,
  Trash2,
  Edit2
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const YEARS = [2024, 2025, 2026];

export default function SalairesManagement({ onNavigate }) {
  const { hasPermission } = usePermissions();
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [evolutionData, setEvolutionData] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [salRes, empRes, dashRes] = await Promise.all([
        axios.get(`${API}/rh/salaires?mois=${selectedMonth}&annee=${selectedYear}`, { headers: headers() }),
        axios.get(`${API}/rh/employes`, { headers: headers() }),
        axios.get(`${API}/rh/dashboard?annee=${selectedYear}`, { headers: headers() })
      ]);
      setSalaries(salRes.data);
      setEmployees(empRes.data);
      if (dashRes.data.evolutionMasse) {
        setEvolutionData(dashRes.data.evolutionMasse);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSalaries = useMemo(() => {
    return salaries.filter(s => 
      `${s.employe?.prenom} ${s.employe?.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [salaries, searchTerm]);

  const stats = useMemo(() => {
    const totalNet = salaries.reduce((acc, s) => acc + s.salaireNet, 0);
    const totalPrimes = salaries.reduce((acc, s) => acc + (s.primesTotal || 0), 0);
    const count = salaries.length;
    return { totalNet, totalPrimes, count };
  }, [salaries]);

  const handleGeneratePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text(`Rapport de Paie - ${MONTHS[selectedMonth-1]} ${selectedYear}`, 14, 22);
    
    autoTable(doc, {
      startY: 30,
      head: [['Employé', 'Salaire Base', 'Primes', 'Déductions', 'Net']],
      body: salaries.map(s => [
        `${s.employe?.prenom} ${s.employe?.nom}`,
        `${s.salaireBase.toLocaleString()} TND`,
        `+${(s.primesTotal || 0).toLocaleString()} TND`,
        `-${(s.deductionsTotal || 0).toLocaleString()} TND`,
        `${s.salaireNet.toLocaleString()} TND`
      ]),
    });
    doc.save(`paie-${selectedMonth}-${selectedYear}.pdf`);
  };

  const handleGenerateBatch = async () => {
    const result = await Swal.fire({
      title: 'Générer les salaires ?',
      text: `Voulez-vous générer les fiches de paie pour ${MONTHS[selectedMonth-1]} ${selectedYear} ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, générer',
      confirmButtonColor: '#FFD700'
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`${API}/rh/salaires/batch`, { mois: selectedMonth, annee: selectedYear }, { headers: headers() });
        Swal.fire('Succès', 'Salaires générés', 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de la génération', 'error');
      }
    }
  };

  const handleDeleteSalaire = (id) => {
    Swal.fire({
      title: 'Supprimer ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E53E3E',
      confirmButtonText: 'Oui'
    }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.delete(`${API}/rh/salaires/${id}`, { headers: headers() });
        fetchData();
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-stats-grid">
        <div className="rh-stat-card">
          <div className="rh-stat-icon-wrapper purple">
            <Wallet size={24} />
          </div>
          <div className="rh-stat-info">
            <span className="rh-stat-value">{stats.totalNet.toLocaleString()} TND</span>
            <span className="rh-stat-label">Masse salariale nette</span>
          </div>
        </div>
        <div className="rh-stat-card">
          <div className="rh-stat-icon-wrapper green">
            <TrendingUp size={24} />
          </div>
          <div className="rh-stat-info">
            <span className="rh-stat-value">+{stats.totalPrimes.toLocaleString()} TND</span>
            <span className="rh-stat-label">Total des primes</span>
          </div>
        </div>
        <div className="rh-stat-card">
          <div className="rh-stat-icon-wrapper blue">
            <Users size={24} />
          </div>
          <div className="rh-stat-info">
            <span className="rh-stat-value">{stats.count}</span>
            <span className="rh-stat-label">Employés payés</span>
          </div>
        </div>
      </div>

      <div className="rh-main-grid" style={{ gridTemplateColumns: '1fr 350px' }}>
        <div className="rh-table-card">
          <div className="rh-table-header">
            <div>
              <h2 style={{ margin: 0 }}>Gestion de la Paie</h2>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <select 
                  className="rh-modal-input" 
                  style={{ width: 'auto', padding: '6px 12px' }}
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(Number(e.target.value))}
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select 
                  className="rh-modal-input" 
                  style={{ width: 'auto', padding: '6px 12px' }}
                  value={selectedYear}
                  onChange={e => setSelectedYear(Number(e.target.value))}
                >
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="rh-action-btn" onClick={handleGeneratePDF} title="Télécharger PDF">
                <Download size={18} />
              </button>
              <button className="rh-add-btn" onClick={handleGenerateBatch}>
                <Plus size={18} /> Générer
              </button>
            </div>
          </div>

          <div className="rh-table-container">
            <table className="rh-table">
              <thead>
                <tr>
                  <th>Employé</th>
                  <th>Salaire Base</th>
                  <th>Primes</th>
                  <th>Net</th>
                  <th>Statut</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSalaries.map(s => (
                  <tr key={s._id}>
                    <td>
                      <div className="rh-user-cell">
                        <div className="rh-employee-avatar-small">{(s.employe?.prenom?.[0] || '') + (s.employe?.nom?.[0] || '')}</div>
                        <span className="rh-user-name">{s.employe?.prenom} {s.employe?.nom}</span>
                      </div>
                    </td>
                    <td>{s.salaireBase.toLocaleString()} TND</td>
                    <td><span className="rh-amount-pos">+{s.primesTotal?.toLocaleString() || 0}</span></td>
                    <td><span className="rh-amount-total">{s.salaireNet.toLocaleString()} TND</span></td>
                    <td>
                      <span className={`rh-status-badge ${s.statut === 'paye' ? 'approved' : 'pending'}`}>
                        {s.statut}
                      </span>
                    </td>
                    <td>
                      <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="rh-action-btn edit" onClick={() => onNavigate('rh-salaire-detail', s._id)}>
                          <Eye size={16} />
                        </button>
                        <button className="rh-action-btn delete" onClick={() => handleDeleteSalaire(s._id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rh-widgets-col">
          <div className="rh-chart-card">
            <h3>Évolution de la Masse</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="totalNet" stroke="#0F2343" fill="#EBF4FF" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="rh-widget-card">
            <div className="rh-widget-header">
              <h3>Dernières fiches</h3>
            </div>
            <div className="rh-list">
              {salaries.slice(0, 5).map(s => (
                <div key={s._id} className="rh-list-item">
                  <div className="rh-item-main">
                    <div className="rh-avatar">{s.employe?.prenom?.[0]}</div>
                    <div className="rh-item-text">
                      <span className="rh-item-name">{s.employe?.prenom} {s.employe?.nom}</span>
                      <span className="rh-item-sub">{s.salaireNet.toLocaleString()} TND</span>
                    </div>
                  </div>
                  <FileText size={16} color="var(--rh-text-muted)" style={{ cursor: 'pointer' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
